import type {
  AdditionalForcesAndMomentsData,
  BuriedCheckData,
  ChecksLongData,
  ChecksShortData,
  ComplianceChecksData,
  ComplianceChecksSection,
  ComponentDetailsData,
  ComponentDetailsSection,
  DefectDetailsData,
  DefectDetailsSection,
  DiameterEncompassingEntireThinDefectData,
  ExternalPressureVacuumData,
  ExtraLayersData,
  JobDetailsData,
  OperatingConditionsData,
  OperatingConditionsSection,
  PinholeDetailsData,
  PressureCyclesData,
  RepairKitsData,
  RepairKitsSection,
  RepairOptionsData,
  SlotDetailsData,
  SlotPinholeSectionType,
  UTReadingData,
} from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { CreateUpdateCompositeJobSections, ExpandableCards } from "@stryker/core/types/jobs";
import type {
  AdditionalJobDetailsData,
  AdditionalJobDetailsSection,
  ContactInformationData,
  ContactInformationSection,
  DesignConditionsData,
  DesignConditionsSection,
  NotesDetailsData,
} from "@stryker/core/types/sections";
import { create } from "zustand";

const initialDefectDetails = {
  defects: [],
};
const initialState = {
  jobIdForDuplication: null,
  sections: {
    jobDetails: {
      status: "open",
      isDataFilled: false,
      data: {
        branch: "",
        job_number: null,
        plant_name: "",
        service_name: "",
        design_code: "",
        repair_system: null,
        surface_preparation: null,
      },
    },
    additionalJobDetails: {
      status: "blocked",
      isDataFilled: false,
      addMore: false,
      data: {
        unit: "",
        line_id: null,
      },
    },
    contactInformation: {
      status: "blocked",
      isDataFilled: false,
      addMore: false,
      data: {
        contact_info_name: "",
        contact_info_phone: "",
        contact_info_email: "",
      },
    },
    designConditions: {
      status: "blocked",
      isDataFilled: false,
      data: {
        design_pressure: null,
        design_temperature: null,
        unit_for_design_temperature: "",
      },
    },
    operatingConditions: {
      status: "blocked",
      isDataFilled: false,
      data: {
        operating_pressure: null,
        unit_for_installation_temperature: "",
        operating_temp_temperature: null,
        unit_for_operating_temp_temperature: "",
        installation_temperature: null,
      },
    },
    componentDetails: {
      status: "blocked",
      isDataFilled: false,
      data: {
        component_options: [],
        component_material: null,
        components_data: [
          {
            component_nps: null,
            component_diameter: null,
            unit_for_component_diameter: null,
            component_thickness: null,
            unit_for_component_thickness: null,
            component_schedule: null,
            is_flanges: false,
          },
        ],
      },
    },
    defectDetails: {
      status: "blocked",
      isDataFilled: false,
      data: initialDefectDetails,
    },
    repairOptions: {
      status: "blocked",
      isDataFilled: false,
      data: {
        repair_length_options: [],
      },
    },
    repairKits: {
      status: "blocked",
      isDataFilled: false,
      data: {
        repair_length_repair_kit_sizes: [],
      },
    },
    complianceChecks: {
      status: "blocked",
      isDataFilled: false,
      data: {
        desired_layer_count_options: [],
      },
    },
    checksSection: {
      status: "blocked",
      isDataFilled: false,
      data: {
        desired_layer_count: null,
      },
    },
    slotPinholeDetails: undefined,
    diameterEncompassingEntireThinDefectSection: undefined,
    pressureCyclesSection: undefined,
    additionalForcesAndMomentsSection: undefined,
    externalPressureVacuumSection: undefined,
    utReadingSection: undefined,
    buriedDetails: undefined,
    extraLayers: undefined,
    notes: {
      status: "canBeOpened",
      isDataFilled: false,
      data: { additional_notes: [], new_notes: [{ custom_name_of_note: "", custom_note: "" }] },
    },
  },
} satisfies { sections: CreateUpdateCompositeJobSections; jobIdForDuplication: null | number };

export type CUJobStore = {
  jobIdForDuplication: null | string;
  sections: CreateUpdateCompositeJobSections;
  setJobDetails: (payload: { data: JobDetailsData; status: ExpandStatus }) => void;
  setAdditionalJobDetails: (payload: { data?: AdditionalJobDetailsData; status: ExpandStatus }) => void;
  setContactInformation: (payload: { data?: ContactInformationData; status: ExpandStatus }) => void;
  setDesignConditions: (payload: { data: DesignConditionsData; status: ExpandStatus }) => void;
  setOperatingConditions: (payload: { data: OperatingConditionsData; status: ExpandStatus }) => void;
  setComponentDetails: (payload: { data: ComponentDetailsData; status: ExpandStatus }) => void;
  setDefectDetails: (payload: { data: DefectDetailsData; status: ExpandStatus }) => void;
  setRepairOptions: (payload: { data: RepairOptionsData; status: ExpandStatus }) => void;
  setRepairKits: (payload: { data: RepairKitsData; status: ExpandStatus }) => void;
  setCompliantCheck: (payload: { data: ComplianceChecksData; status: ExpandStatus }) => void;
  setChecks: (payload: { data: ChecksShortData | ChecksLongData; status: ExpandStatus }) => void;
  setSlotPinholeDetails: (payload: { data: SlotDetailsData | PinholeDetailsData; status: ExpandStatus }) => void;
  setDiameterEncompassingEntireThinDefectDetails: (payload: {
    data: DiameterEncompassingEntireThinDefectData;
    status: ExpandStatus;
  }) => void;
  setUTReadingDetails: (payload: { data: UTReadingData; status: ExpandStatus }) => void;
  setBuriedDetails: (payload: { data: BuriedCheckData; status: ExpandStatus }) => void;
  setPressureCyclesSection: (payload: { data: PressureCyclesData; status: ExpandStatus }) => void;
  setAdditionalForcesAndMomentsSections: (payload: {
    data: AdditionalForcesAndMomentsData;
    status: ExpandStatus;
  }) => void;
  setExternalPressureVacuumSection: (payload: { data: ExternalPressureVacuumData; status: ExpandStatus }) => void;
  setExtraLayers: (payload: { data: ExtraLayersData; status: ExpandStatus }) => void;
  setNotes: (payload: { data: NotesDetailsData; status: ExpandStatus }) => void;
  toggleExpandableSection: (section: ExpandableCards) => void;
  resetState: () => void;
  setDefaultValues: (data: CreateUpdateCompositeJobSections) => void;
  setJobIdForDuplication: (id: string | null) => void;
};

const useCUCompositeJobStore = create<CUJobStore>((set) => ({
  ...initialState,
  setJobDetails: (payload) =>
    set((state) => {
      const hasRepairSystemChanged = payload.data.repair_system != state.sections.jobDetails.data.repair_system;
      const designConditions: DesignConditionsSection =
        hasRepairSystemChanged && state.sections.designConditions.status !== "blocked"
          ? {
              ...initialState.sections.designConditions,
              status: "canBeOpened",
              data: {
                ...initialState.sections.designConditions.data,
                unit_for_design_temperature: "Fahrenheit",
              },
            }
          : state.sections.designConditions;
      const repairKits: RepairKitsSection = hasRepairSystemChanged
        ? {
            ...initialState.sections.repairKits,
            status: (state.sections.repairKits.status === "blocked" ? "blocked" : "open") as ExpandStatus,
          }
        : state.sections.repairKits;
      const additionalJobDetails: AdditionalJobDetailsSection =
        state.sections.additionalJobDetails.isDataFilled && state.sections.additionalJobDetails.status !== "blocked"
          ? state.sections.additionalJobDetails
          : {
              ...state.sections.additionalJobDetails,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          jobDetails: { data: payload.data, isDataFilled: payload.status === "done", status: payload.status },
          additionalJobDetails,
          designConditions,
          repairKits,
        },
      };
    }),
  setAdditionalJobDetails: (payload) =>
    set((state) => {
      const contactInformation: ContactInformationSection =
        state.sections.contactInformation.isDataFilled && state.sections.contactInformation.status !== "blocked"
          ? state.sections.contactInformation
          : {
              ...state.sections.contactInformation,
              status: "open",
            };
      if (payload.data) {
        return {
          sections: {
            ...state.sections,
            additionalJobDetails: {
              data: payload.data,
              addMore: true,
              isDataFilled: payload.status === "done",
              status: payload.status,
            },
            contactInformation,
          },
        };
      }
      return {
        sections: {
          ...state.sections,
          additionalJobDetails: {
            ...state.sections.additionalJobDetails,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          contactInformation,
        },
      };
    }),
  setContactInformation: (payload) =>
    set((state) => {
      const designConditions: DesignConditionsSection =
        state.sections.designConditions.isDataFilled && state.sections.designConditions.status !== "blocked"
          ? state.sections.designConditions
          : {
              ...state.sections.designConditions,
              status: "open",
            };
      if (payload.data) {
        return {
          sections: {
            ...state.sections,
            contactInformation: {
              data: payload.data,
              addMore: true,
              isDataFilled: payload.status === "done",
              status: payload.status,
            },
            designConditions,
          },
        };
      }
      return {
        sections: {
          ...state.sections,
          contactInformation: {
            ...state.sections.contactInformation,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          designConditions,
        },
      };
    }),
  setDesignConditions: (payload) =>
    set((state) => {
      const operatingConditions: OperatingConditionsSection =
        state.sections.operatingConditions.isDataFilled && state.sections.operatingConditions.status !== "blocked"
          ? state.sections.operatingConditions
          : {
              ...state.sections.operatingConditions,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          designConditions: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          operatingConditions,
        },
      };
    }),
  setOperatingConditions: (payload) =>
    set((state) => {
      const componentDetails: ComponentDetailsSection =
        state.sections.componentDetails.isDataFilled && state.sections.componentDetails.status !== "blocked"
          ? state.sections.componentDetails
          : {
              ...state.sections.componentDetails,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          operatingConditions: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          componentDetails,
        },
      };
    }),
  setComponentDetails: (payload) =>
    set((state) => {
      const shouldChangeDefectDetails =
        state.sections.componentDetails.data.components_data.length !== payload.data.components_data.length;

      const defectDetails: DefectDetailsSection = state.sections.defectDetails;
      if (!defectDetails.isDataFilled || defectDetails.status === "blocked") {
        defectDetails.status = "open";
      }
      if (shouldChangeDefectDetails) {
        defectDetails.status = "open";
        defectDetails.isDataFilled = false;
        defectDetails.data = initialDefectDetails;
      }

      return {
        sections: {
          ...state.sections,
          componentDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          defectDetails,
        },
      };
    }),
  setDefectDetails: (payload) =>
    set((state) => {
      const repairOptions =
        state.sections.repairOptions.isDataFilled && state.sections.repairOptions.status !== "done"
          ? state.sections.repairOptions
          : {
              ...state.sections.repairOptions,
              status: "open" as ExpandStatus,
            };
      return {
        sections: {
          ...state.sections,
          defectDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          repairOptions,
        },
      };
    }),
  setRepairOptions: (payload) =>
    set((state) => {
      const repairKits =
        state.sections.repairKits.isDataFilled && state.sections.repairKits.status !== "done"
          ? state.sections.repairKits
          : {
              ...state.sections.repairKits,
              status: "open" as ExpandStatus,
            };
      return {
        sections: {
          ...state.sections,
          repairOptions: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          repairKits,
        },
      };
    }),
  setRepairKits: (payload) =>
    set((state) => {
      const complianceChecks: ComplianceChecksSection =
        state.sections.complianceChecks.isDataFilled && state.sections.complianceChecks.status !== "done"
          ? state.sections.complianceChecks
          : {
              ...state.sections.complianceChecks,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          repairKits: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          complianceChecks,
        },
      };
    }),
  setCompliantCheck: (payload) =>
    set((state) => {
      const isNonCompliantCheck =
        payload.data.desired_layer_count_options.length > 0 &&
        !payload.data.desired_layer_count_options.includes("none");
      const isNonCompliantChecksFilled =
        "desired_layer_count" in state.sections.checksSection.data &&
        typeof state.sections.checksSection.data.desired_layer_count === "number";

      const areChecksFilled = state.sections.checksSection.isDataFilled;
      let nextSection: Partial<CreateUpdateCompositeJobSections> = {
        checksSection: areChecksFilled
          ? state.sections.checksSection
          : {
              ...state.sections.checksSection,
              status: "open",
            },
        slotPinholeDetails: isNonCompliantCheck ? undefined : state.sections.slotPinholeDetails,
        buriedDetails: isNonCompliantCheck ? undefined : state.sections.buriedDetails,
        extraLayers: isNonCompliantCheck ? undefined : state.sections.extraLayers,
        diameterEncompassingEntireThinDefectSection: isNonCompliantCheck
          ? undefined
          : state.sections.diameterEncompassingEntireThinDefectSection,
        utReadingSection: isNonCompliantCheck ? undefined : state.sections.utReadingSection,
        additionalForcesAndMomentsSection: isNonCompliantCheck
          ? undefined
          : state.sections.additionalForcesAndMomentsSection,
        externalPressureVacuumSection: isNonCompliantCheck ? undefined : state.sections.externalPressureVacuumSection,
      };

      if (
        (areChecksFilled && isNonCompliantCheck && !isNonCompliantChecksFilled) ||
        (!isNonCompliantCheck && isNonCompliantChecksFilled)
      ) {
        nextSection = {
          ...nextSection,
          checksSection: {
            ...nextSection.checksSection,
            // @ts-expect-error
            data: initialState.sections.checksSection.data,
            status: "open",
          },
        };
      }

      return {
        sections: {
          ...state.sections,
          complianceChecks: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          ...nextSection,
        },
      };
    }),
  setChecks: (payload) =>
    set((state) => {
      const shouldShowPinhole = "is_pinhole_leak" in payload.data && payload.data.is_pinhole_leak === "yes";
      const shouldShowSlot = "is_pinhole_leak" in payload.data && payload.data.is_pinhole_leak === "no";
      const shouldShowBuried = "is_component_buried" in payload.data && payload.data.is_component_buried === "yes";
      const shouldShowDiameterEncompassingEntireThinDefect =
        "per_code_line_leaking_do_you_know_diameter" in payload.data &&
        payload.data.per_code_line_leaking_do_you_know_diameter === "yes";
      const shouldShowUTReading =
        "is_ut_reading_less_than_04" in payload.data && payload.data.is_ut_reading_less_than_04 === "no";
      const shouldShowExtraLayers = "add_extraLayers" in payload.data && payload.data.add_extraLayers === "yes";
      const shouldShowPressureCycles =
        "are_the_expected_cycles_more_than_7000_over_design_life" in payload.data &&
        payload.data.are_the_expected_cycles_more_than_7000_over_design_life === "yes";
      const shouldShowAdditionalForcesAndMomentsSection =
        "do_you_want_to_take_into_account_any_additional_forces_and_moments" in payload.data &&
        payload.data.do_you_want_to_take_into_account_any_additional_forces_and_moments === "yes";
      const shouldShowExternalPressureVacuumSection =
        "do_you_want_to_design_for_external_loads_or_vacuum" in payload.data &&
        payload.data.do_you_want_to_design_for_external_loads_or_vacuum === "yes";

      // initial state of new sections
      const slotPinholeState = state.sections.slotPinholeDetails;
      const diameterEncompassingEntireThinDefecteState = state.sections.diameterEncompassingEntireThinDefectSection;
      const utReadingState = state.sections.utReadingSection;
      const buriedState = state.sections.buriedDetails;
      const extraLayersState = state.sections.extraLayers;
      const pressureCyclesSection = state.sections.pressureCyclesSection;
      const additionalForcesAndMomentsSection = state.sections.additionalForcesAndMomentsSection;
      const externalPressureVacuumSection = state.sections.externalPressureVacuumSection;

      //  initial state of all sections
      let nextSection = {
        slotPinholeDetails: shouldShowSlot || shouldShowPinhole ? slotPinholeState : undefined,
        buriedDetails: shouldShowBuried ? buriedState : undefined,
        extraLayers: shouldShowExtraLayers ? extraLayersState : undefined,
        diameterEncompassingEntireThinDefectSection: shouldShowDiameterEncompassingEntireThinDefect
          ? diameterEncompassingEntireThinDefecteState
          : undefined,
        utReadingSection: shouldShowUTReading ? utReadingState : undefined,
        pressureCyclesSection: shouldShowPressureCycles ? pressureCyclesSection : undefined,
        additionalForcesAndMomentsSection: shouldShowAdditionalForcesAndMomentsSection
          ? additionalForcesAndMomentsSection
          : undefined,
        externalPressureVacuumSection: shouldShowExternalPressureVacuumSection
          ? externalPressureVacuumSection
          : undefined,
      };

      if (shouldShowPressureCycles) {
        const number_of_cycles = pressureCyclesSection?.data.number_of_cycles;
        const min_cycle_pressure = pressureCyclesSection?.data.min_cycle_pressure;
        const max_cycle_pressure = pressureCyclesSection?.data.max_cycle_pressure;
        nextSection = {
          ...nextSection,
          pressureCyclesSection: {
            data: {
              number_of_cycles: number_of_cycles ?? "",
              min_cycle_pressure: min_cycle_pressure ?? "",
              max_cycle_pressure: max_cycle_pressure ?? "",
            },
            isDataFilled: pressureCyclesSection?.isDataFilled ?? false,
            status: pressureCyclesSection?.isDataFilled ? "done" : "open",
          },
        };
      }

      if (shouldShowAdditionalForcesAndMomentsSection) {
        const applied_sheared_load = additionalForcesAndMomentsSection?.data.applied_sheared_load;
        const applied_axial_load = additionalForcesAndMomentsSection?.data.applied_axial_load;
        const applied_tortional_moment = additionalForcesAndMomentsSection?.data.applied_tortional_moment;
        const applied_axial_moment = additionalForcesAndMomentsSection?.data.applied_axial_moment;
        nextSection = {
          ...nextSection,
          additionalForcesAndMomentsSection: {
            data: {
              applied_sheared_load: applied_sheared_load ?? "",
              applied_axial_load: applied_axial_load ?? "",
              applied_tortional_moment: applied_tortional_moment ?? "",
              applied_axial_moment: applied_axial_moment ?? "",
            },
            isDataFilled: additionalForcesAndMomentsSection?.isDataFilled ?? false,
            status: additionalForcesAndMomentsSection?.isDataFilled ? "done" : "open",
          },
        };
      }

      if (shouldShowExternalPressureVacuumSection) {
        const external_pressure_vacuum = externalPressureVacuumSection?.data.external_pressure_vacuum;
        nextSection = {
          ...nextSection,
          externalPressureVacuumSection: {
            data: {
              external_pressure_vacuum: external_pressure_vacuum ?? "",
            },
            isDataFilled: externalPressureVacuumSection?.isDataFilled ?? false,
            status: externalPressureVacuumSection?.isDataFilled ? "done" : "open",
          },
        };
      }

      if (shouldShowPinhole) {
        const pinhole_diameter = (slotPinholeState?.data as PinholeDetailsData)?.pinhole_diameter;
        const data = slotPinholeState?.data ?? {};
        const prevIsPinhole = "pinhole_diameter" in data;
        nextSection = {
          ...nextSection,
          slotPinholeDetails: {
            data: {
              pinhole_diameter: pinhole_diameter ?? null,
            },
            isDataFilled: prevIsPinhole ? (slotPinholeState?.isDataFilled ?? false) : false,
            status: slotPinholeState?.isDataFilled ? "done" : "open",
            type: "pinhole",
          },
        };
      }

      if (shouldShowSlot) {
        const slot_diameter = (slotPinholeState?.data as SlotDetailsData)?.slot_diameter;
        const data = slotPinholeState?.data ?? {};
        const prevIsSlot = "slot_diameter" in data;
        nextSection = {
          ...nextSection,
          slotPinholeDetails: {
            data: { slot_diameter: slot_diameter ?? null },
            isDataFilled: prevIsSlot ? (slotPinholeState?.isDataFilled ?? false) : false,
            status: slotPinholeState?.isDataFilled ? "done" : "open",
            type: "slot",
          },
        };
      }

      if (shouldShowDiameterEncompassingEntireThinDefect) {
        const saved_pinhole_2 = diameterEncompassingEntireThinDefecteState?.data?.pinhole_2;
        nextSection = {
          ...nextSection,
          diameterEncompassingEntireThinDefectSection: {
            data: { pinhole_2: saved_pinhole_2 ?? null },
            isDataFilled: diameterEncompassingEntireThinDefecteState?.isDataFilled ?? false,
            status: diameterEncompassingEntireThinDefecteState?.isDataFilled ? "done" : "open",
          },
        };
      }

      if (shouldShowUTReading) {
        const saved_ut_readings = utReadingState?.data?.ut_readings;
        nextSection = {
          ...nextSection,
          utReadingSection: {
            data: { ut_readings: saved_ut_readings ?? null },
            isDataFilled: utReadingState?.isDataFilled ?? false,
            status: utReadingState?.isDataFilled ? "done" : "open",
          },
        };
      }

      if (shouldShowBuried) {
        const buriedData = buriedState?.data;
        nextSection = {
          ...nextSection,
          buriedDetails: {
            ...state.sections.buriedDetails,
            data: {
              buried_depth: buriedData?.buried_depth ?? null,
              unit_for_buried_check: buriedData?.unit_for_buried_check ?? "",
            },
            isDataFilled: buriedState?.isDataFilled ?? false,
            status: buriedState?.isDataFilled ? "done" : "open",
          },
        };
      }

      if (shouldShowExtraLayers) {
        const extra_layers = extraLayersState?.data.extra_layers;
        nextSection = {
          ...nextSection,
          extraLayers: {
            ...state.sections.extraLayers,
            data: { extra_layers: extra_layers ?? null },
            isDataFilled: extraLayersState?.isDataFilled ?? false,
            status: extraLayersState?.isDataFilled ? "done" : "open",
          },
        };
      }

      return {
        ...state,
        sections: {
          ...state.sections,
          checksSection: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          } as any,
          ...nextSection,
        },
      };
    }),
  setSlotPinholeDetails: (payload) =>
    set((state) => {
      let type: SlotPinholeSectionType = "pinhole";
      if ("slot_diameter" in payload.data) {
        type = "slot";
      }

      return {
        sections: {
          ...state.sections,
          slotPinholeDetails: {
            type,
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setBuriedDetails: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          buriedDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setDiameterEncompassingEntireThinDefectDetails: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          diameterEncompassingEntireThinDefectSection: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setUTReadingDetails: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          utReadingSection: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setPressureCyclesSection: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          pressureCyclesSection: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setAdditionalForcesAndMomentsSections: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          additionalForcesAndMomentsSection: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setExternalPressureVacuumSection: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          externalPressureVacuumSection: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  setExtraLayers: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          extraLayers: { data: payload.data, isDataFilled: payload.status === "done", status: payload.status },
        },
      };
    }),
  setNotes: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          notes: { data: payload.data, isDataFilled: payload.status === "done", status: payload.status },
        },
      };
    }),
  toggleExpandableSection: (section) =>
    set((state) => {
      const selectedStatus = state.sections[section];
      const previousState = selectedStatus?.status;
      let newSectionStatus = previousState === "open" ? "canBeOpened" : "open";
      if (newSectionStatus === "canBeOpened" && selectedStatus?.isDataFilled) {
        newSectionStatus = "done";
      }
      return {
        sections: {
          ...state.sections,
          [section]: {
            ...state.sections[section],
            status: newSectionStatus,
          },
        },
      };
    }),
  resetState: () =>
    set(() => {
      // localStorage.removeItem("create-update-storage");
      // Return only the initial state data without preserving actions
      return initialState;
    }),
  setDefaultValues: (payload) =>
    set(() => {
      return { sections: payload };
    }),
  setJobIdForDuplication: (payload) =>
    set((state) => {
      return { ...state, jobIdForDuplication: payload };
    }),
}));

export default useCUCompositeJobStore;
